import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

const BackupManagement = () => {
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [actionLoading, setActionLoading] = useState(null); // filename текущей операции
    const [message, setMessage] = useState({ text: '', type: '' });

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const loadBackups = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.getBackups();
            // Бэкенд может вернуть массив напрямую или объект с полем
            const list = Array.isArray(data) ? data : (data.backups || data.data || []);
            // Сортируем по дате создания (новые сверху)
            list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setBackups(list);
        } catch (error) {
            showMessage('Ошибка загрузки списка резервных копий', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadBackups();
    }, [loadBackups]);

    const handleCreateBackup = async () => {
        if (!window.confirm('Создать резервную копию базы данных?')) return;
        setCreating(true);
        try {
            await api.createBackup();
            showMessage('Резервная копия успешно создана', 'success');
            await loadBackups();
        } catch (error) {
            showMessage('Ошибка создания резервной копии', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleRestore = async (filename) => {
        if (!window.confirm(
            `Вы уверены, что хотите восстановить базу данных из копии:\n"${filename}"?\n\nВсе текущие данные будут заменены!`
        )) return;

        setActionLoading(filename + '_restore');
        try {
            await api.restoreBackup(filename);
            showMessage(`База данных успешно восстановлена из копии "${filename}"`, 'success');
        } catch (error) {
            showMessage('Ошибка восстановления из резервной копии', 'error');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDelete = async (filename) => {
        if (!window.confirm(`Вы уверены, что хотите удалить резервную копию:\n"${filename}"?`)) return;

        setActionLoading(filename + '_delete');
        try {
            await api.deleteBackup(filename);
            showMessage(`Резервная копия "${filename}" удалена`, 'success');
            await loadBackups();
        } catch (error) {
            showMessage('Ошибка удаления резервной копии', 'error');
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '—';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    };

    const formatSize = (bytes) => {
        if (!bytes && bytes !== 0) return '—';
        if (bytes === 0) return '0 Б';
        const units = ['Б', 'КБ', 'МБ', 'ГБ'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
    };

    const getTypeBadge = (type) => {
        if (type === 'manual') {
            return (
                <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '0.78rem',
                    fontWeight: '600',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    border: '1px solid #c3e6cb'
                }}>
                    Ручная
                </span>
            );
        }
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '0.78rem',
                fontWeight: '600',
                backgroundColor: '#cce5ff',
                color: '#004085',
                border: '1px solid #b8daff'
            }}>
                Авто
            </span>
        );
    };

    return (
        <div className="backup-management">
            {/* Заголовок и кнопка создания */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                gap: '1rem'
            }}>
                <div>
                    <h3 style={{ margin: 0, color: '#2c3e50' }}>Резервные копии базы данных</h3>
                    <p style={{ margin: '0.25rem 0 0', color: '#6c757d', fontSize: '0.9rem' }}>
                        Управление резервными копиями. Автоматические копии создаются раз в сутки и хранятся 14 дней.
                    </p>
                </div>
                <button
                    onClick={handleCreateBackup}
                    disabled={creating}
                    className="btn-primary"
                    style={{ whiteSpace: 'nowrap' }}
                >
                    {creating ? '⏳ Создание...' : '💾 Создать резервную копию'}
                </button>
            </div>

            {/* Сообщение */}
            {message.text && (
                <div className={`message ${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Таблица резервных копий */}
            <div style={{
                background: 'white',
                borderRadius: '8px',
                border: '1px solid #dee2e6',
                overflow: 'hidden'
            }}>
                <div style={{
                    padding: '0.75rem 1rem',
                    background: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{ fontWeight: '600', color: '#495057' }}>
                        Список резервных копий
                    </span>
                    <button
                        onClick={loadBackups}
                        disabled={loading}
                        className="btn-secondary"
                        style={{ padding: '0.25rem 0.75rem', fontSize: '0.85rem' }}
                    >
                        {loading ? '⏳' : '🔄'} Обновить
                    </button>
                </div>

                {loading ? (
                    <div className="loading" style={{ padding: '3rem' }}>
                        Загрузка резервных копий...
                    </div>
                ) : backups.length === 0 ? (
                    <div className="no-data" style={{ padding: '3rem' }}>
                        Резервные копии не найдены
                    </div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Имя файла</th>
                                    <th style={{ ...thStyle, width: '100px', textAlign: 'center' }}>Тип</th>
                                    <th style={{ ...thStyle, width: '180px' }}>Дата создания</th>
                                    <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>Размер</th>
                                    <th style={{ ...thStyle, width: '180px', textAlign: 'center' }}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {backups.map((backup, index) => {
                                    const isRestoring = actionLoading === backup.filename + '_restore';
                                    const isDeleting = actionLoading === backup.filename + '_delete';
                                    const isBusy = isRestoring || isDeleting;

                                    return (
                                        <tr
                                            key={backup.filename}
                                            style={{
                                                backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                                                transition: 'background-color 0.15s'
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#e8f4fd'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = index % 2 === 0 ? 'white' : '#f8f9fa'}
                                        >
                                            <td style={tdStyle}>
                                                <span style={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.85rem',
                                                    color: '#2c3e50',
                                                    wordBreak: 'break-all'
                                                }}>
                                                    {backup.filename}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {getTypeBadge(backup.type)}
                                            </td>
                                            <td style={tdStyle}>
                                                <span style={{ fontSize: '0.9rem', color: '#495057' }}>
                                                    {formatDate(backup.createdAt)}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                                                    {formatSize(backup.sizeBytes)}
                                                </span>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => handleRestore(backup.filename)}
                                                        disabled={isBusy || !!actionLoading}
                                                        title="Восстановить базу данных из этой копии"
                                                        style={{
                                                            padding: '0.25rem 0.6rem',
                                                            fontSize: '0.8rem',
                                                            backgroundColor: isRestoring ? '#6c757d' : '#17a2b8',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: (isBusy || !!actionLoading) ? 'not-allowed' : 'pointer',
                                                            opacity: (isBusy || !!actionLoading) ? 0.7 : 1,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {isRestoring ? '⏳ Восстановление...' : '↩ Восстановить'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(backup.filename)}
                                                        disabled={isBusy || !!actionLoading}
                                                        title="Удалить эту резервную копию"
                                                        style={{
                                                            padding: '0.25rem 0.6rem',
                                                            fontSize: '0.8rem',
                                                            backgroundColor: isDeleting ? '#6c757d' : '#e74c3c',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: (isBusy || !!actionLoading) ? 'not-allowed' : 'pointer',
                                                            opacity: (isBusy || !!actionLoading) ? 0.7 : 1,
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {isDeleting ? '⏳ Удаление...' : '🗑 Удалить'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Информационный блок */}
            <div style={{
                marginTop: '1.5rem',
                padding: '1rem',
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                fontSize: '0.9rem',
                color: '#856404'
            }}>
                <strong>⚠ Внимание:</strong> Восстановление из резервной копии заменит все текущие данные в базе данных.
                Убедитесь, что выбрана правильная копия перед восстановлением.
            </div>
        </div>
    );
};

const thStyle = {
    padding: '0.75rem 1rem',
    textAlign: 'left',
    backgroundColor: '#2c3e50',
    color: 'white',
    fontWeight: '600',
    fontSize: '0.9rem',
    borderBottom: '2px solid #dee2e6',
    position: 'sticky',
    top: 0,
    zIndex: 1
};

const tdStyle = {
    padding: '0.75rem 1rem',
    borderBottom: '1px solid #dee2e6',
    verticalAlign: 'middle'
};

export default BackupManagement;
